/**
 * reset-password.tsx — Reset-Password-Route (AUTH-Block-Fix)
 *
 * Formular zum Einloesen des Passwort-Reset-Links (?token=...) aus der Mail,
 * die von Api::V2::PasswordsController#create ausgeloest wird. Vormals hatte
 * die SPA UEBERHAUPT KEINE Route dafuer — der Devise-Mailer verlinkte auf eine
 * Legacy-Monolith-View (edit_password_url), der Passwort-Reset-Flow war an
 * dieser Stelle komplett unterbrochen.
 *
 * PATCH /api/v2/auth/password mit { reset_password_token, password, password_confirmation }.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

function buildResetPasswordSchema(t: (key: string) => string) {
  return z
    .object({
      password: z.string().min(12, t('validation.passwordMinLength')),
      password_confirmation: z.string().min(1, t('validation.passwordConfirmEmpty')),
    })
    .refine((d) => d.password === d.password_confirmation, {
      message: t('validation.passwordMismatch'),
      path: ['password_confirmation'],
    })
}

type ResetPasswordInput = z.infer<ReturnType<typeof buildResetPasswordSchema>>

const RESET_PASSWORD_URL = `${import.meta.env.VITE_API_BASE_URL}/api/v2/auth/password`

export const Route = createFileRoute('/reset-password')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: ResetPasswordPage,
})

function ResetPasswordPage() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const { token } = Route.useSearch()
  const [formError, setFormError] = useState<string | null>(null)

  const resetPasswordSchema = useMemo(() => buildResetPasswordSchema(t), [t])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
  })

  async function onSubmit(data: ResetPasswordInput) {
    setFormError(null)
    try {
      const res = await fetch(RESET_PASSWORD_URL, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reset_password_token: token,
          password: data.password,
          password_confirmation: data.password_confirmation,
        }),
      })

      if (res.ok) {
        void navigate({ to: '/login', search: { passwordReset: true } })
        return
      }

      if (res.status === 422) {
        const json = (await res.json()) as { errors?: Record<string, string[]> }
        const tokenErrors = json.errors?.reset_password_token
        if (tokenErrors && tokenErrors.length > 0) {
          setFormError(t('resetPassword.errors.invalidOrExpiredToken'))
        } else {
          const firstMessage = json.errors ? Object.values(json.errors)[0]?.[0] : undefined
          setFormError(firstMessage ?? t('resetPassword.errors.unexpectedError'))
        }
        return
      }

      setFormError(t('resetPassword.errors.unexpectedError'))
    } catch {
      setFormError(t('resetPassword.errors.networkError'))
    }
  }

  return (
    <div className="auth-shell" data-testid="reset-password-page">
      {/* Linke Spalte: Formular */}
      <div className="auth-form-wrap">
        <div className="auth-form">
          {/* Logo-Mark */}
          <div className="row" style={{ gap: 10 }}>
            <div className="logo-mark">H</div>
            <div style={{ fontWeight: 600 }}>HeliumTracker</div>
          </div>

          <div>
            <h1 style={{ fontSize: 28 }}>{t('resetPassword.title')}</h1>
            <p className="muted" style={{ margin: '6px 0 0' }}>
              {t('resetPassword.subtitle')}
            </p>
          </div>

          {!token && (
            <div
              role="alert"
              aria-live="polite"
              className="alert alert-error"
              data-testid="missing-token-message"
            >
              {t('resetPassword.missingToken')}
            </div>
          )}

          {formError && (
            <div
              role="alert"
              aria-live="polite"
              className="alert alert-error"
              data-testid="form-error"
            >
              {formError}
            </div>
          )}

          {token && (
            <form onSubmit={handleSubmit(onSubmit)} noValidate aria-label={t('resetPassword.formAriaLabel')} style={{ display: 'contents' }}>
              <div>
                <label className="label" htmlFor="new-password">{t('resetPassword.passwordLabel')}</label>
                <input
                  id="new-password"
                  type="password"
                  className="input"
                  placeholder={t('resetPassword.passwordPlaceholder')}
                  aria-label={t('resetPassword.passwordAriaLabel')}
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? 'new-password-error' : undefined}
                  {...register('password')}
                />
                {errors.password && (
                  <span
                    id="new-password-error"
                    role="alert"
                    aria-live="polite"
                    className="field-error"
                  >
                    {errors.password.message}
                  </span>
                )}
              </div>

              <div>
                <label className="label" htmlFor="new-password-confirmation">{t('resetPassword.passwordConfirmLabel')}</label>
                <input
                  id="new-password-confirmation"
                  type="password"
                  className="input"
                  aria-label={t('resetPassword.passwordConfirmAriaLabel')}
                  aria-invalid={!!errors.password_confirmation}
                  aria-describedby={errors.password_confirmation ? 'new-password-confirm-error' : undefined}
                  {...register('password_confirmation')}
                />
                {errors.password_confirmation && (
                  <span
                    id="new-password-confirm-error"
                    role="alert"
                    aria-live="polite"
                    className="field-error"
                  >
                    {errors.password_confirmation.message}
                  </span>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-lg"
                disabled={isSubmitting}
                aria-label={t('resetPassword.submitAriaLabel')}
                style={{ width: '100%' }}
                data-testid="submit-button"
              >
                {isSubmitting ? t('resetPassword.submitting') : t('resetPassword.submitLabel')}
              </button>
            </form>
          )}

          <div className="muted" style={{ fontSize: 13, textAlign: 'center' }}>
            <a href="/login" style={{ color: 'var(--primary)' }}>
              {t('resetPassword.backToLogin')}
            </a>
          </div>
        </div>
      </div>

      {/* Rechte Spalte: Branding-Aside */}
      <aside className="auth-aside">
        <div className="row" style={{ gap: 10 }}>
          <div className="logo-mark">H</div>
          <div style={{ fontWeight: 600 }}>HeliumTracker</div>
        </div>

        <div>
          <h2 style={{ fontSize: 30, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
            {t('resetPassword.aside.headline')}
          </h2>
          <p className="muted" style={{ marginTop: 14, fontSize: 14, maxWidth: 480, lineHeight: 1.7 }}>
            {t('resetPassword.aside.description')}
          </p>
        </div>

        <div className="muted" style={{ fontSize: 11 }}>
          {t('aside.copyright')}
        </div>
      </aside>
    </div>
  )
}
